import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Config from '../utils/Config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  fireConfetti,
  scorePassword,
} from '../utils/functions';
import "./Login.css";
import './ResetPassword.css';

const CreateAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [messageClass, setMessageClass] = useState("warning hidden"); // Default hidden
  const messageRef = useRef(null);
  const confirmRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  // ✅ State for form fields & timezones
  const [formData, setFormData] = useState({
    currentPassword: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordReset, setPasswordReset] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  // ✅ Focus on first input field
  useEffect(() => {
    const focusPassword = (e) => {
      const confirmInput = confirmRef.current;
      if (confirmInput && confirmInput.contains(e.target)) {
        return; // Don't refocus if confirmPassword is clicked
      }

      inputRef.current?.focus(); // inputRef points to password field
    };

    // Initial focus
    inputRef.current?.focus();

    // Attach global click
    document.addEventListener("click", focusPassword);
    return () => document.removeEventListener("click", focusPassword);
  }, []);

  const validateForm = () => {
    if (!formData.password.trim()) {
      messageRef.current.innerHTML = "❌ Password is required.";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
      return false;
    }
    if (formData.currentPassword == formData.password) {
      messageRef.current.innerHTML = "❌ New password can't be same as current password!";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
      return false;
    }
    if (!formData.confirmPassword.trim()) {
      messageRef.current.innerHTML = "❌ Confirm password is required.";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
      return false;
    }
    if (formData.password.length < 8) {
      messageRef.current.innerHTML = "❌ Password must be at least 8 characters long.";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      messageRef.current.innerHTML = "❌ Passwords do not match!";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // setFormData({ ...formData, [e.target.name]: e.target.value });

    if (name === "password") {
      const strengthScore = scorePassword(value);
      setPasswordScore(strengthScore);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_RESET_PASSWORD}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          currentPassword: formData.currentPassword,
          password: formData.password,
        }),
      });

      // Parse the response JSON
      const data = await response.json();

      if (response.ok) {
        messageRef.current.innerHTML = `✅ ${data.message}`;
        setMessageClass("success");
        messageRef.current.style.display = "block";
        setPasswordReset(true);
        fireConfetti();
      } else {
        messageRef.current.innerHTML = `❌ ${data.message}`;
        setMessageClass("warning");
        messageRef.current.style.display = "block";
      }
    } catch (error) {
      messageRef.current.innerHTML = `❌ Error resetting password: ${error.message}`;
      setMessageClass("warning");
      messageRef.current.style.display = "block";
    }
  };

  return (
    <div className="login-container" id="create-account">
      <div className="login-box" id="login-container">
        {passwordReset ? (
          <>
            <h2>🎉 Password Reset Successfully!</h2>
            <p>Your password has been successfully reset.</p>
            <button className="login-button" onClick={() => navigate("/login")}>
              Go to Login Now
            </button>
          </>
        ) : (
          <>
            <h2>
              <FontAwesomeIcon icon={"lock"} />&nbsp;&nbsp;Reset Password
            </h2>
            <div ref={messageRef} className={messageClass} id="message" />
            <form onSubmit={handleRegister}>

              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  ref={inputRef}
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password toggle-password-reset"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? "🙈 Hide" : "👁️ Show"}
                </button>
              </div>
              {formData.password && (
                <div className="strength-bar-container">
                  <div className={`strength-bar fill-${passwordScore}`} />
                </div>
              )}
              {formData.password && (
                <p className="password-hint">
                  Use at least 8 characters with letters, numbers, and symbols.
                </p>
              )}
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                autocomplete="confirm-password"
                ref={confirmRef}
              />
              {formData.confirmPassword && (
                <p
                  className={`password-match ${passwordsMatch ? "match" : "no-match"
                    }`}
                >
                  {passwordsMatch ? "✅ Passwords match" : "❌ Passwords do not match"}
                </p>
              )}

              <button type="submit" className="login-button">
                Reset
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateAccount;
